import * as yup from "yup";
import {Language, UserDTO} from "@/api/data-contracts";
import {useMemo} from "react";
import {languageOptions, salutationOptions} from "@/datacaches";
import {Constants} from "@/api/constants";
import {EMAIL_REGEX, PHONE_NUMBER_REGEX} from "@/regex";
import {useTranslation} from "react-i18next";
import {UserI18N} from "@/translations";

export type UserEditDTO = Pick<
    UserDTO,
    "email" | "salutation" | "firstName" | "lastName" | "telephone" | "fax" | "language"
>;

/**
 * Builds the user-profile edit schema. `availableLanguages` restricts the `language` field's
 * `.oneOf(...)` validation to languages with a maintained/uploaded language file (see
 * `useAvailableLanguages` in `datacaches.ts`) — defaults to the static `languageOptions`
 * (baseline: GERMAN only) so existing callers without the new profile dropdown keep working.
 * An empty `availableLanguages` (the cache's loading/error state) falls back to the same static
 * `languageOptions` baseline internally, so `.oneOf(...)` never validates against an empty set.
 */
export const useUserEditSchema = (availableLanguages: Language[] = languageOptions) => {
    const { t } = useTranslation();
    return useMemo(() => {
        // `useAvailableLanguages()` resolves to `[]` while loading and permanently after a
        // fetch error. An empty `.oneOf([])` would reject any value — including an already
        // persisted language — and since `useModelState.internal()` re-validates the whole
        // schema on every field change, editing any other field would then spontaneously fail
        // on `language`. Fall back to the static baseline so `.oneOf(...)` never validates
        // against an empty set.
        const languagesForValidation = availableLanguages.length > 0 ? availableLanguages : languageOptions;
        const schema = yup.object().shape({
            email: yup
                .string()
                .required(t(UserI18N.form.validation.emailRequired))
                .min(
                    Constants.EMAIL_LENGTH_MIN,
                    t(UserI18N.form.validation.emailMin, {min: Constants.EMAIL_LENGTH_MIN})
                )
                .max(
                    Constants.EMAIL_LENGTH_MAX,
                    t(UserI18N.form.validation.emailMax, {max: Constants.EMAIL_LENGTH_MAX})
                )
                .matches(EMAIL_REGEX, t(UserI18N.form.validation.emailInvalid)),

            salutation: yup
                .string()
                .required(t(UserI18N.form.validation.salutationRequired))
                .oneOf(salutationOptions, t(UserI18N.form.validation.salutationInvalid)),

            firstName: yup
                .string()
                .required(t(UserI18N.form.validation.firstNameRequired))
                .min(
                    Constants.FIRSTNAME_LENGTH_MIN,
                    t(UserI18N.form.validation.firstNameMin, {min: Constants.FIRSTNAME_LENGTH_MIN})
                )
                .max(
                    Constants.FIRSTNAME_LENGTH_MAX,
                    t(UserI18N.form.validation.firstNameMax, {max: Constants.FIRSTNAME_LENGTH_MAX})
                ),

            lastName: yup
                .string()
                .required(t(UserI18N.form.validation.lastNameRequired))
                .min(
                    Constants.LASTNAME_LENGTH_MIN,
                    t(UserI18N.form.validation.lastNameMin, {min: Constants.LASTNAME_LENGTH_MIN})
                )
                .max(
                    Constants.LASTNAME_LENGTH_MAX,
                    t(UserI18N.form.validation.lastNameMax, {max: Constants.LASTNAME_LENGTH_MAX})
                ),

            telephone: yup
                .string()
                .required(t(UserI18N.form.validation.phoneRequired))
                .min(
                    Constants.PHONE_NUMBER_LENGTH_MIN,
                    t(UserI18N.form.validation.phoneMin, {min: Constants.PHONE_NUMBER_LENGTH_MIN})
                )
                .max(
                    Constants.PHONE_NUMBER_LENGTH_MAX,
                    t(UserI18N.form.validation.phoneMax, {max: Constants.PHONE_NUMBER_LENGTH_MAX})
                )
                .matches(PHONE_NUMBER_REGEX, t(UserI18N.form.validation.phoneInvalid)),

            fax: yup
                .string()
                .optional()
                .nullable()
                .transform((value) => (value === "" ? null : value))
                .min(
                    Constants.PHONE_NUMBER_LENGTH_MIN,
                    t(UserI18N.form.validation.faxMin, {min: Constants.PHONE_NUMBER_LENGTH_MIN})
                )
                .max(
                    Constants.PHONE_NUMBER_LENGTH_MAX,
                    t(UserI18N.form.validation.faxMax, {max: Constants.PHONE_NUMBER_LENGTH_MAX})
                )
                .matches(PHONE_NUMBER_REGEX, t(UserI18N.form.validation.faxInvalid)),

            language: yup
                .string()
                .required(t(UserI18N.form.validation.languageRequired))
                .oneOf(languagesForValidation, t(UserI18N.form.validation.languageRequired))
        });
        const typed = schema as yup.ObjectSchema<UserEditDTO>;
        return typed as yup.ObjectSchema<UserDTO>;
    }, [t, availableLanguages]);
};
